import { useState, useEffect } from 'react';
import { useAdminCrud } from '@/hooks/useAdminCrud';
import { AdminHeader, LoadingGrid, EmptyState, FormActions } from '@/components/admin/AdminComponents';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Radio, Edit, Trash2 } from 'lucide-react';

const initialForm = {
  model_name: '', short_description: '', main_image: '', coverage_range: '',
  coverage_type: '', typical_use_cases: '', key_benefits: '', status: 'Active'
};

const AdminUltrasonic = () => {
  const { items, loading, isDialogOpen, editingItem, setIsDialogOpen, createItem, updateItem, deleteItem, openCreate, openEdit, closeDialog } = useAdminCrud('ultrasonic-models', 'Ultrasonic model');
  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    if (editingItem) {
      setFormData({
        model_name: editingItem.model_name, short_description: editingItem.short_description,
        main_image: editingItem.main_image || '', coverage_range: editingItem.coverage_range || '',
        coverage_type: editingItem.coverage_type || '', typical_use_cases: editingItem.typical_use_cases || '',
        key_benefits: editingItem.key_benefits || '', status: editingItem.status
      });
    } else {
      setFormData(initialForm);
    }
  }, [editingItem]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const success = editingItem ? await updateItem(editingItem.id, formData) : await createItem(formData);
    if (success) setFormData(initialForm);
  };

  const update = (field) => (e) => setFormData(prev => ({ ...prev, [field]: e.target.value }));
  const updateSelect = (field) => (v) => setFormData(prev => ({ ...prev, [field]: v }));

  return (
    <div data-testid="admin-ultrasonic">
      <AdminHeader
        title="Ultrasonic Models" subtitle="Manage ultrasonic deterrence products" addLabel="Add Model"
        isDialogOpen={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) { closeDialog(); setFormData(initialForm); } }}
        dialogTitle={editingItem ? 'Edit Ultrasonic Model' : 'Add Ultrasonic Model'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Model Name *</Label>
              <Input value={formData.model_name} onChange={update('model_name')} required placeholder="e.g., DF-88607" />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={formData.status} onValueChange={updateSelect('status')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Legacy">Legacy</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Short Description *</Label>
            <Textarea value={formData.short_description} onChange={update('short_description')} required rows={2} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Coverage Range</Label>
              <Input value={formData.coverage_range} onChange={update('coverage_range')} placeholder="e.g., 150-200m" />
            </div>
            <div className="space-y-2">
              <Label>Coverage Type</Label>
              <Select value={formData.coverage_type} onValueChange={updateSelect('coverage_type')}>
                <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Single-direction">Single-direction</SelectItem>
                  <SelectItem value="Multi-direction">Multi-direction</SelectItem>
                  <SelectItem value="360°">360°</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Main Image URL</Label>
            <Input value={formData.main_image} onChange={update('main_image')} placeholder="https://..." />
          </div>
          <div className="space-y-2">
            <Label>Typical Use Cases</Label>
            <Input value={formData.typical_use_cases} onChange={update('typical_use_cases')} placeholder="Airports, Runways, Power Stations" />
          </div>
          <div className="space-y-2">
            <Label>Key Benefits (one per line)</Label>
            <Textarea value={formData.key_benefits} onChange={update('key_benefits')} rows={4} />
          </div>
          <FormActions onCancel={closeDialog} isEditing={!!editingItem} />
        </form>
      </AdminHeader>

      {loading ? <LoadingGrid count={3} /> : items.length === 0 ? (
        <EmptyState Icon={Radio} title="No ultrasonic models yet" subtitle="Add your first ultrasonic model" onAdd={openCreate} addLabel="Add Model" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(item => (
            <div key={item.id} className="bg-white rounded-sm border border-slate-200 overflow-hidden">
              <div className="aspect-video bg-slate-100">
                {item.main_image ? (
                  <img src={item.main_image} alt={item.model_name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-400"><Radio className="w-12 h-12" /></div>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-slate-900 font-mono">{item.model_name}</h3>
                  <span className="text-xs px-2 py-1 rounded bg-purple-100 text-purple-700">{item.coverage_range || 'N/A'}</span>
                </div>
                <p className="text-slate-600 text-sm mb-2 line-clamp-2">{item.short_description}</p>
                <p className="text-xs text-slate-500 mb-4">{item.coverage_type}</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => openEdit(item)}><Edit className="w-3 h-3 mr-1" />Edit</Button>
                  <Button variant="outline" size="sm" onClick={() => deleteItem(item.id)} className="text-red-600 hover:bg-red-50"><Trash2 className="w-3 h-3 mr-1" />Delete</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminUltrasonic;
